import React, { useEffect, useState, useRef } from 'react';
import {
  SHORTCUT_EVENT,
  type PlayerShortcut,
} from '../../turncard/useTurnShortcuts';
import PlayButton from './PlayButton';
import SlowerButton from './SlowerButton';
import styles from './AudioPlayer.module.css';
import { usePromptPlayer } from '../../speech/react';
import { canAutoPlay } from '../../speech';

interface AudioPlayerProps {
  audioUrl: string;
  slowerAudioUrl?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  /** The clip couldn't be played (e.g. it failed to load or decode). */
  onError?: (error: unknown) => void;
  /**
   * Start the clip when the player appears. The browser may refuse a clip that
   * no tap started; that is not an error, the Play button is still there.
   */
  autoPlay?: boolean;
  /** Extra controls shown after Play and Slower (e.g. the "?" help button). */
  extraControls?: React.ReactNode;
  className?: string;
  /**
   * This is the player the keyboard shortcuts drive (#307): R plays the clip again, S plays it
   * slower, both through the same playback path as a tap (the key press is the gesture).
   */
  shortcuts?: boolean;
  disabled?: boolean;
}

/**
 * Play / Slower controls for a prompt clip. Playback goes through the shared
 * Web Audio prompt player (see `speech/promptPlayer.ts` for why not <audio> or
 * speechSynthesis). Tapping Play while playing stops the clip; the next tap
 * plays it from the start.
 */
export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  slowerAudioUrl,
  onPlay,
  onPause,
  onEnded,
  onError,
  autoPlay = false,
  extraControls,
  className,
  shortcuts = false,
  disabled = false,
}) => {
  const [isSlower, setIsSlower] = useState(false);
  // Which speed the current clip is, so a failed slower clip resets the toggle.
  const playingSlowerRef = useRef(false);
  // True while a clip no tap started is loading: its failure stays silent.
  const autoStartedRef = useRef(false);
  const player = usePromptPlayer({
    onStarted: () => {
      autoStartedRef.current = false;
      onPlay?.();
    },
    onEnded: () => {
      setIsSlower(false);
      onPause?.();
      onEnded?.();
    },
    onStoppedElsewhere: () => onPause?.(),
    onError: (error) => {
      if (autoStartedRef.current) {
        autoStartedRef.current = false;
        return;
      }
      console.error('Error playing audio:', error);
      if (playingSlowerRef.current) setIsSlower(false);
      onError?.(error);
    },
  });
  const isPlaying = player.isPlaying;

  /** Start a clip; must run inside the tap handler (iOS gesture rule). */
  const start = (url: string, slower: boolean): void => {
    playingSlowerRef.current = slower;
    player.play(url, slower ? 'slower' : 'natural');
  };

  useEffect(() => {
    // Only when an earlier tap left audio running; otherwise the Play button.
    if (!autoPlay || !audioUrl || !canAutoPlay()) return;
    autoStartedRef.current = true;
    start(audioUrl, false);
    // Once, when the player appears.
  }, []);

  const rootRef = useRef<HTMLDivElement>(null);
  const shortcutRef = useRef<(action: PlayerShortcut) => void>(() => undefined);
  shortcutRef.current = (action) => {
    if (disabled || !audioUrl) return;
    autoStartedRef.current = false;
    if (action === 'again') {
      start(audioUrl, false);
    } else if (slowerAudioUrl) {
      setIsSlower(true);
      start(slowerAudioUrl, true);
    }
  };
  useEffect(() => {
    const node = rootRef.current;
    if (!shortcuts || !node) return;
    const onShortcut = (e: Event): void =>
      shortcutRef.current((e as CustomEvent<PlayerShortcut>).detail);
    node.addEventListener(SHORTCUT_EVENT, onShortcut);
    return (): void => node.removeEventListener(SHORTCUT_EVENT, onShortcut);
  }, [shortcuts]);

  const handlePlayClick = (): void => {
    autoStartedRef.current = false;
    if (isPlaying) {
      player.stop();
      onPause?.();
      return;
    }
    const slower = isSlower && !!slowerAudioUrl;
    start(slower ? slowerAudioUrl : audioUrl, slower);
  };

  const handleSlowerClick = (): void => {
    /* istanbul ignore next -- SlowerButton only renders when slowerAudioUrl is set */
    if (!slowerAudioUrl) return;
    setIsSlower(true);
    if (isPlaying) start(slowerAudioUrl, true);
  };

  return (
    <div
      ref={rootRef}
      className={`${styles.audioPlayer} ${className || ''}`}
      data-shortcut-player={shortcuts ? '' : undefined}
    >
      <div className={styles.audioControls}>
        <PlayButton
          onClick={handlePlayClick}
          isPlaying={isPlaying}
          disabled={disabled || !audioUrl}
          shortcut={shortcuts ? 'R' : undefined}
        />
        {!!slowerAudioUrl && (
          <SlowerButton
            onClick={handleSlowerClick}
            disabled={disabled || isSlower}
            shortcut={shortcuts ? 'S' : undefined}
          />
        )}
        {extraControls}
      </div>
      {isPlaying && (
        <div className={styles.audioIndicator}>
          {isSlower ? 'Playing slower...' : 'Playing...'}
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
